
import { Outlet, Navigate, useLocation, Link } from "react-router-dom";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/contexts/ProfileContext";
import { Loader2 } from "lucide-react";
import { EmployerNav } from "@/components/nav/EmployerNav";
import { CandidateNav } from "@/components/nav/CandidateNav";
import { MobileBottomNav } from "@/components/nav/MobileBottomNav";
import { useIsMobile } from "@/hooks/use-mobile";
import { ActiveTestsBanner } from "@/components/shared/ActiveTestsBanner";


type StableRoleType = 'employer' | 'candidate' | 'administrator' | 'loading' | 'error' | 'unknown';

export const MainLayout = React.memo(() => {
  const { user, loading: authLoading, authStateReady } = useAuth();
  const { profile, isLoading: profileLoading, error: profileError, forceRefreshProfile } = useProfile();
  const isMobile = useIsMobile();
  const location = useLocation();
  
  // Track initial load completion
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // Track when we've already attempted a profile refresh
  const [hasAttemptedRefresh, setHasAttemptedRefresh] = useState(false);
  // Track last valid location to prevent redirects on tab switch
  const lastValidLocationRef = useRef(location.pathname);
  // Track if we're handling a tab switch event
  const [isTabSwitchEvent, setIsTabSwitchEvent] = useState(false);
  
  // Store the determined role to prevent flicker during profile refetch
  const [stableRole, setStableRole] = useState<StableRoleType>(
    authLoading || profileLoading ? 'loading' : (profile?.role || 'unknown')
  );
  
  // Handle tab visibility changes to detect tab switching
  useEffect(() => {
    const handleTabVisibility = () => {
      if (document.visibilityState === 'visible' && user) {
        console.log('[MainLayout] Tab became visible, treating as tab switch event');
        setIsTabSwitchEvent(true);
        // Reset after a short delay
        setTimeout(() => setIsTabSwitchEvent(false), 2000);
      }
    };
    
    document.addEventListener('visibilitychange', handleTabVisibility);
    return () => document.removeEventListener('visibilitychange', handleTabVisibility);
  }, [user]);
  
  // Monitoring logic for debugging role changes
  const prevRoleRef = useRef<string | undefined>(profile?.role);
  useEffect(() => {
    if (profile?.role !== prevRoleRef.current) {
      console.log(`[MainLayout] Role changed from '${prevRoleRef.current}' to '${profile?.role}'`);
      prevRoleRef.current = profile?.role;
    }
  }, [profile?.role]);

  // Save the last valid location whenever we have a user and auth is ready
  useEffect(() => {
    if (user && authStateReady && !authLoading) {
      lastValidLocationRef.current = location.pathname;
      console.log('[MainLayout] Saving last valid location:', lastValidLocationRef.current);
    }
  }, [user, authStateReady, authLoading, location.pathname]);

  // Update initial load state once both auth and profile loading are complete
  useEffect(() => {
    if (!authLoading && (!user || !profileLoading)) {
      if (!initialLoadComplete) {
        console.log('[MainLayout] Initial load marked as complete.');
        setInitialLoadComplete(true);
      }
    }
  }, [authLoading, user, profileLoading, initialLoadComplete]);

  // Update stableRole when profile data changes and is not loading
  useEffect(() => {
    if (authStateReady && !authLoading) {
      if (!user) {
        // Don't change role during tab switch events
        if (!isTabSwitchEvent && stableRole !== 'unknown') {
          console.log('[MainLayout] No user, setting stableRole to unknown');
          setStableRole('unknown');
        }
      } else if (profileError) {
        if (stableRole !== 'error') {
          console.error('[MainLayout] Profile error detected, setting stableRole to error.');
          setStableRole('error');
        }
      } else if (profileLoading) {
        // Keep existing role if we already have a valid role
        if (!['employer', 'candidate', 'administrator'].includes(stableRole)) {
          setStableRole('loading');
        }
      } else if (profile?.role) {
        if (profile.role !== stableRole) {
          console.log(`[MainLayout] Updating stableRole from ${stableRole} to ${profile.role}`);
          setStableRole(profile.role);
        }
      } else if (user && !profileLoading && !profileError && !profile?.role && stableRole !== 'unknown') {
        // Handle case where profile loads but has no role
        console.warn('[MainLayout] Profile loaded but role is missing, setting stableRole to unknown.');
        setStableRole('unknown');
      }
    } else {
      // Auth is not ready or still loading, maintain loading state only if not tab switch
      if (stableRole !== 'loading' && !isTabSwitchEvent) {
        setStableRole('loading');
      }
    }
  }, [user, profile, profileLoading, profileError, stableRole, authLoading, authStateReady, isTabSwitchEvent]);

  // Memoize these functions to prevent unnecessary re-renders
  const mobileTopNav = useMemo(() => (
    <nav className="fixed top-0 left-0 right-0 z-[100] bg-background border-b border-border">
      <div className="container max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="/lovable-uploads/e7e0cd62-757f-4e57-b3bf-42cffdc8d843.png" 
              alt="Testera Logo" 
              className="h-8 w-8"
            />
            <span className="text-2xl font-bold text-foreground tracking-tight">
              Testera
            </span>
          </Link>
        </div>
      </div>
    </nav>
  ), []);

  // Memoize the nav components based on role
  const navComponent = useMemo(() => {
    if (stableRole === 'employer' || stableRole === 'administrator') {
      return <EmployerNav />;
    } else if (stableRole === 'candidate') {
      return <CandidateNav />;
    } else {
      // Default to employer view if profile role is not recognized
      console.warn('[MainLayout] Unknown role or null profile, defaulting to employer view:', stableRole);
      return <EmployerNav />;
    }
  }, [stableRole]);

  // --- Render Logic ---

  // 1. Show loading if auth isn't ready OR initial auth is loading
  // Skip loading screen during tab switch events if we have a stable role
  if ((!authStateReady || authLoading) && !isTabSwitchEvent) {
    console.log('[MainLayout] Showing loading screen. Auth state ready:', authStateReady, 'Auth loading:', authLoading);
    return (
      <div className="flex justify-center items-center min-h-screen bg-background">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  // 2. Handle Unauthenticated - redirect to login with the return URL
  // Only redirect if auth state is ready (stable), there's no user, and this is not a tab switch
  if (authStateReady && !authLoading && !user && !isTabSwitchEvent) {
    console.log('[MainLayout] Auth ready, no user found. Redirecting to login.');
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  // 3. Handle intermediate states after auth is ready
  // Don't show intermediate states during tab switch if we have a stable role
  if ((stableRole === 'loading' || stableRole === 'error' || stableRole === 'unknown') && 
      (!isTabSwitchEvent || !['employer', 'candidate', 'administrator'].includes(prevRoleRef.current as any))) {
    // If we have user but no role yet, show appropriate loading/error state
    if (stableRole === 'unknown' && user && !hasAttemptedRefresh) {
      console.warn('[MainLayout] User authenticated, profile loaded, but role is missing. Attempting refresh once.');
      // Only try to refresh once to prevent infinite loops
      setHasAttemptedRefresh(true);
      setTimeout(() => {
        forceRefreshProfile();
      }, 500);
    }

    const message = stableRole === 'loading' ? 'Loading user profile...' :
                    stableRole === 'error' ? 'Error loading profile.' :
                    'Cannot determine user role.';
    
    console.log(`[MainLayout] Showing intermediate state: ${stableRole}`);
    return (
      <div className="min-h-screen flex flex-col w-full">
        {/* Minimal header with just the logo and theme toggle */}
        <nav className="fixed top-0 left-0 right-0 z-[100] bg-background border-b border-border">
          <div className="container max-w-7xl mx-auto px-6 flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/lovable-uploads/e7e0cd62-757f-4e57-b3bf-42cffdc8d843.png" 
                alt="Testera Logo" 
                className="h-6 w-6"
              />
              <span className="text-2xl font-bold text-foreground">Testera</span>
            </Link>
          </div>
        </nav>
        <main className="flex-1 pt-16 flex items-center justify-center text-center px-4">
          <div>
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">{message}</p>
            {stableRole === 'error' && (
              <button 
                onClick={() => window.location.reload()} 
                className="mt-4 px-3 py-1 bg-secondary text-secondary-foreground text-sm rounded"
              >
                Refresh
              </button>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Redirect employers away from the jobs page
  if ((stableRole === 'employer' || stableRole === 'administrator') && 
      (location.pathname === '/jobs' || location.pathname === '/job')) {
    console.log('[MainLayout] Employer attempting to access jobs page. Redirecting to test management.');
    return <Navigate to="/test-management" replace />;
  }

  // 4. Render full layout based on stable role
  console.log('[MainLayout] Rendering full layout for role:', stableRole);

  return (
    <div className="min-h-screen flex flex-col w-full">
      {isMobile ? mobileTopNav : navComponent}
      {stableRole === 'candidate' && <ActiveTestsBanner />}
      {/* Main content area - pt-16 for fixed nav */}
      {/* mb-16 for fixed bottom nav on mobile */}
      <main className={`flex-1 pt-16 ${isMobile ? "mb-16" : ""}`}>
        <Outlet /> {/* Child routes will render here */}
      </main>
      {/* Render mobile bottom nav based on confirmed role */}
      {isMobile && <MobileBottomNav />}
    </div>
  );
});

MainLayout.displayName = 'MainLayout';
